import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ShoppingCart, Package, Users, Settings, Tag, ArrowRight, X } from 'lucide-react';
import { ADMIN_ROUTES } from '@wonderherbals/shared';
import { useApiQuery } from '../hooks/useApi';

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CommandPalette({ isOpen, onClose }: CommandPaletteProps) {
  const [query, setQuery] = useState('');
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 50);
    } else {
      setQuery('');
    }
  }, [isOpen]);

  // Global Ctrl+K / Cmd+K listener
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        if (isOpen) onClose();
        else onClose(); // parent handles toggle
      }
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Fetch matching products and orders when search query >= 2 chars
  const { data: searchResults } = useApiQuery<any>(
    ['admin', 'command-search', query],
    `/admin/products?search=${encodeURIComponent(query)}&limit=5`,
    { enabled: query.trim().length >= 2 }
  );

  const products = (searchResults as any)?.data || [];

  const QUICK_LINKS = [
    { title: 'Orders Command', path: ADMIN_ROUTES.ORDERS, icon: ShoppingCart, category: 'Navigation' },
    { title: 'Add New Product', path: ADMIN_ROUTES.PRODUCT_NEW, icon: Package, category: 'Actions' },
    { title: 'Inventory Control', path: `${ADMIN_ROUTES.PRODUCTS}?tab=inventory`, icon: Package, category: 'Actions' },
    { title: 'Customer Directory', path: ADMIN_ROUTES.CUSTOMERS, icon: Users, category: 'Navigation' },
    { title: 'Discount Coupons', path: ADMIN_ROUTES.COUPONS, icon: Tag, category: 'Marketing' },
    { title: 'Store Settings', path: ADMIN_ROUTES.SETTINGS, icon: Settings, category: 'Settings' },
  ];

  const filteredLinks = QUICK_LINKS.filter(link =>
    link.title.toLowerCase().includes(query.toLowerCase())
  );

  const handleSelect = (path: string) => {
    navigate(path);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-start justify-center pt-20 p-4 animate-fade-in"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl border border-admin-border w-full max-w-2xl overflow-hidden animate-scale-up"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Search Header */}
        <div className="flex items-center px-4 py-3.5 border-b border-admin-border gap-3">
          <Search className="w-5 h-5 text-admin-forest shrink-0" />
          <input
            ref={inputRef}
            type="text"
            placeholder="Search orders, products, customers, or jump to page... (Esc to exit)"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full bg-transparent text-sm font-medium text-admin-text placeholder-admin-text-muted outline-none"
          />
          {query && (
            <button onClick={() => setQuery('')} className="p-1 rounded-md hover:bg-gray-100 text-admin-text-muted">
              <X className="w-4 h-4" />
            </button>
          )}
          <kbd className="hidden sm:inline-block px-2 py-0.5 text-2xs font-mono font-semibold text-gray-500 bg-gray-100 border border-gray-300 rounded shadow-xs">
            ESC
          </kbd>
        </div>

        {/* Search Results / Quick Links */}
        <div className="max-h-[380px] overflow-y-auto p-2 divide-y divide-gray-100">
          {/* Matching Products */}
          {products.length > 0 && (
            <div className="py-2">
              <p className="px-3 pb-1.5 text-[10px] font-bold uppercase tracking-wider text-admin-text-muted">
                Products ({products.length})
              </p>
              {products.map((p: any) => (
                <button
                  key={p.id}
                  onClick={() => handleSelect(`${ADMIN_ROUTES.PRODUCT_EDIT.replace(':id', p.id)}`)}
                  className="w-full flex items-center justify-between px-3 py-2 rounded-xl text-left hover:bg-admin-forest/5 group transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center text-xs overflow-hidden border border-admin-border">
                      {p.primaryImageUrl ? (
                        <img src={p.primaryImageUrl} alt={p.name} className="w-full h-full object-cover" />
                      ) : (
                        '🌿'
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-admin-text group-hover:text-admin-forest">{p.name}</p>
                      <p className="text-2xs text-admin-text-muted font-mono">{p.sku || p.category}</p>
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-admin-text-muted opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>
              ))}
            </div>
          )}

          {/* Quick Actions & Navigation */}
          <div className="py-2">
            <p className="px-3 pb-1.5 text-[10px] font-bold uppercase tracking-wider text-admin-text-muted">
              {query ? 'Matching Commands' : 'Quick Actions'}
            </p>
            {filteredLinks.length > 0 ? (
              filteredLinks.map((link) => {
                const Icon = link.icon;
                return (
                  <button
                    key={link.title}
                    onClick={() => handleSelect(link.path)}
                    className="w-full flex items-center justify-between px-3 py-2 rounded-xl text-left hover:bg-admin-forest/5 group transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-7 h-7 rounded-lg bg-admin-forest/10 flex items-center justify-center text-admin-forest">
                        <Icon className="w-4 h-4" />
                      </div>
                      <span className="text-sm font-medium text-admin-text group-hover:text-admin-forest">
                        {link.title}
                      </span>
                    </div>
                    <span className="text-2xs font-semibold text-admin-text-muted uppercase tracking-wider">
                      {link.category}
                    </span>
                  </button>
                );
              })
            ) : (
              <p className="px-3 py-4 text-center text-xs text-admin-text-muted">
                No matching results found for "{query}"
              </p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-4 py-2.5 bg-gray-50 border-t border-admin-border flex items-center justify-between text-2xs text-admin-text-muted">
          <div className="flex items-center gap-3">
            <span>Press <kbd className="px-1 py-0.5 bg-white border border-gray-200 rounded">↵</kbd> to select</span>
            <span><kbd className="px-1 py-0.5 bg-white border border-gray-200 rounded">ESC</kbd> to close</span>
          </div>
          <span className="font-semibold text-admin-forest">Wonder Herbals Admin</span>
        </div>
      </div>
    </div>
  );
}
